import { ThemeKey } from "@smartsheet/design-tokens";
import React, { ButtonHTMLAttributes, forwardRef, MouseEventHandler, ReactNode, useCallback, useMemo } from "react";
import { useTheme } from "../../theme/utils/useTheme";
import { createFocusRingStyles } from "../../util/createFocusRingStyles";
import type { CreateFocusRingProps } from "../../util/focusRing";
import { ClickableStyled } from "./styled/ClickableStyled";
import { clickableTheme } from "./styled/clickableTheme";

export interface ClickableProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> {
    children: ReactNode;
    /** Client ID to be included with any logging calls. If not provided, no logs will be created */
    clientId?: string;
    /**
     * @deprecated This prop will be removed from the Clickable in lodestar-core V6+ in an effort to standardize focus ring styles across the library.
     */
    focusRingOptions?: Partial<CreateFocusRingProps>;
    isDisabled?: boolean;
    omitFocusRing?: boolean;
}

/**
 * Provides the basic stuff for any clickables, include a style reset!
 */
export const Clickable = forwardRef<HTMLButtonElement, ClickableProps>(
    (
        {
            children,
            clientId,
            omitFocusRing = false,
            focusRingOptions = {},
            isDisabled = false,
            onClick: providedOnClick,
            onDoubleClick: providedOnDoubleClick,
            tabIndex = 0,
            type = "button",
            ...rest
        },
        ref
    ) => {
        const { key } = useTheme();
        const themeMode = key as ThemeKey;
        const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
            (e) => {
                if (isDisabled) {
                    e.preventDefault();
                    return;
                }
                if (providedOnClick) {
                    providedOnClick(e);
                }
            },
            [isDisabled, providedOnClick]
        );

        const onDoubleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
            (e) => {
                if (isDisabled) {
                    e.preventDefault();
                    return;
                }
                if (providedOnDoubleClick) {
                    providedOnDoubleClick(e);
                }
            },
            [isDisabled, providedOnDoubleClick]
        );

        const { offset, includeFocusPseudoClass, borderWidth, radius, ...restFocusRingOptions } = focusRingOptions;

        const focusRingStyles = useMemo(
            () =>
                omitFocusRing
                    ? ""
                    : createFocusRingStyles({
                          theme: themeMode,
                          gap: offset ?? 1,
                          componentRadius: radius ?? clickableTheme.borderRadius(themeMode),
                          componentBorderWidth: borderWidth ?? 1,
                          focusSelector: includeFocusPseudoClass ? ":focus" : undefined,
                          ringWidth: borderWidth ?? undefined,
                          ...restFocusRingOptions,
                      }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [omitFocusRing, ...Object.values(focusRingOptions)]
        );

        return (
            <ClickableStyled
                {...rest}
                isDisabled={isDisabled}
                aria-disabled={isDisabled}
                data-client-id={clientId}
                focusRingStyles={focusRingStyles}
                ref={ref}
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                tabIndex={tabIndex}
                type={type}
            >
                {children}
            </ClickableStyled>
        );
    }
);

Clickable.displayName = "Clickable";
